import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Expand, Mail, Phone, ShoppingBag } from "lucide-react";
import { PhotoZoom } from "@/components/site/PhotoZoom";
import { toast } from "sonner";
import { useCart } from "@/lib/cart";
import { CartButton, CartDrawer, QtyControl } from "@/components/site/Cart";
import { Drawer, DrawerContent, DrawerDescription, DrawerTitle } from "@/components/ui/drawer";
import { api } from "@/lib/api";
import { imgUrl, useContent } from "@/lib/content";
import { PageLoader } from "@/pages/Home";
import { Media } from "@/components/site/Media";

import { ShopTabs } from "@/components/site/ShopTabs";

const price = (p, placeholder) => (p.price == null ? placeholder : `$${Number(p.price).toFixed(2)}`);

const AddToCart = ({ p, onAdded }) => {
  const cart = useCart();
  const [qty, setQty] = useState(1);
  if (p.price == null) return null;
  if (p.sold_out) return <p className="shop-soldout-note" data-testid="product-sold-out">Sold out — call or email us to be told when it's back.</p>;
  return (
    <div className="shop-buy">
      <QtyControl qty={qty} onChange={(q) => setQty(Math.max(1, Math.min(20, q)))} testId="product-qty" />
      <button type="button" className="btn gold" onClick={() => { cart.add(p, qty); toast.success(`${p.name} added to your bag`); onAdded(); }} data-testid="product-add-to-cart-button">
        <ShoppingBag size={14} /> Add to bag
      </button>
    </div>
  );
};

const ProductSheet = ({ p, onClose, c, onAdded }) => {
  const [img, setImg] = useState(0);
  const [zoom, setZoom] = useState(false);
  useEffect(() => { setImg(0); setZoom(false); }, [p?.id]);
  const keepOpen = (e) => { if (zoom) e.preventDefault(); };
  return (
    <Drawer open={!!p} onOpenChange={(o) => !o && !zoom && onClose()} shouldScaleBackground={false}>
      <DrawerContent className="sheet shop-sheet" data-testid="product-sheet" onPointerDownOutside={keepOpen} onInteractOutside={keepOpen}
        onEscapeKeyDown={(e) => { if (zoom) { e.preventDefault(); setZoom(false); } }}>
        {p && (
          <div className="shop-sheet-inner">
            <button type="button" className="shop-sheet-photo" onClick={() => p.images.length && setZoom(true)} aria-label="View full-screen photo" data-testid="product-sheet-zoom-button">
              <img src={imgUrl(p.images[img] || p.images[0], 900)} alt={p.name} data-testid="product-sheet-image" />
              {p.images.length > 0 && <span className="shop-zoom-hint"><Expand size={13} /> Tap to zoom</span>}
            </button>
            {zoom && <PhotoZoom images={p.images} start={img} alt={p.name} onClose={() => setZoom(false)} />}
            {p.images.length > 1 && (
              <div className="shop-thumbs">
                {p.images.map((u, i) => (
                  <button key={u} type="button" aria-pressed={i === img} onClick={() => setImg(i)} data-testid={`product-thumb-${i}`}><img src={imgUrl(u, 160)} alt="" /></button>
                ))}
              </div>
            )}
            <p className="eyebrow">{p.brand}</p>
            <DrawerTitle className="sheet-title" data-testid="product-sheet-name">{p.name}</DrawerTitle>
            <DrawerDescription className="sheet-desc">{[p.category, p.size].filter(Boolean).join(" · ")}</DrawerDescription>
            <p className="shop-price" data-testid="product-sheet-price">{price(p, c.shop.price_placeholder)}{p.sold_out && <span className="shop-soldout-pill">Sold out</span>}</p>
            <p className="shop-desc">{p.description}</p>
            {p.instructions && <div className="shop-howto" data-testid="product-sheet-instructions"><p className="eyebrow">How to use</p><p className="shop-desc">{p.instructions}</p></div>}
            <AddToCart key={p.id} p={p} onAdded={onAdded} />
            <p className="muted-sm">{c.shop.enquire_label}:</p>
            <div className="shop-actions">
              <a className="btn gold" href={`tel:${c.contact.phone_call}`} data-testid="product-call-button"><Phone size={14} /> Call</a>
              <a className="btn light" href={`mailto:${c.contact.booking_email}?subject=${encodeURIComponent(`Shop enquiry: ${p.brand} ${p.name}`)}`} data-testid="product-email-button"><Mail size={14} /> Email</a>
            </div>
          </div>
        )}
      </DrawerContent>
    </Drawer>
  );
};

export default function Shop() {
  const c = useContent().data;
  const { data } = useQuery({ queryKey: ["products"], queryFn: async () => (await api.get("/products")).data });
  const [filter, setFilter] = useState("All");
  const [open, setOpen] = useState(null);
  const [bag, setBag] = useState(false);
  const cats = useMemo(() => ["All", ...[...new Set((data || []).map((p) => p.category).filter(Boolean))].sort()], [data]);
  const brands = useMemo(() => [...new Set((data || []).map((p) => p.brand).filter(Boolean))], [data]);
  if (!c || !data) return <PageLoader />;
  const list = data.filter((p) => filter === "All" || p.category === filter || p.brand === filter);

  return (
    <div className="wm shop-page" data-testid="shop-page">
      <header className="look-top">
        <Link to="/" className="look-back" data-testid="shop-back-link"><ArrowLeft size={18} /> Home</Link>
        <Link to="/" className="logo logo-black"><img src="/brand/logo-black.png" alt="Wax Moi Day Spa" width="835" height="240" /></Link>
      </header>
      <section className="shop-hero wrap">
        <p className="eyebrow">{c.shop.eyebrow}</p>
        <h1>{c.shop.title}</h1>
        <p>{c.shop.text}</p>
      </section>
      {cats.length > 1 && <ShopTabs cats={cats} data={data} filter={filter} setFilter={setFilter} />}
      {brands.length > 0 && (
        <div className="shop-brands wrap" data-testid="shop-brand-filters">
          {brands.map((f) => (
            <button key={f} type="button" aria-pressed={filter === f} onClick={() => setFilter(filter === f ? "All" : f)} data-testid={`shop-filter-${f.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{f}</button>
          ))}
        </div>
      )}
      <div className="shop-grid wrap" data-testid="shop-grid">
        {list.map((p) => (
          <button key={p.id} type="button" className="product" onClick={() => setOpen(p)} data-testid={`product-card-${p.id}`}>
            <div className="product-photo"><Media src={p.images[0]} alt={p.name} w={500} />{p.sold_out && <span className="product-soldout" data-testid={`product-sold-out-badge-${p.id}`}>Sold out</span>}</div>
            <small>{p.brand}</small>
            <strong>{p.name}</strong>
            <span className="product-price">{price(p, c.shop.price_placeholder)}</span>
          </button>
        ))}
      </div>
      <ProductSheet p={open} onClose={() => setOpen(null)} c={c} onAdded={() => { setOpen(null); setTimeout(() => setBag(true), 300); }} />
      <CartButton onClick={() => setBag(true)} />
      <CartDrawer open={bag} onClose={() => setBag(false)} />
    </div>
  );
}
